export const PATH_TILE_ATLAS_IDS = ['dirt', 'water', 'flowers', 'brick_road'] as const;

export type PathTileAtlasId = typeof PATH_TILE_ATLAS_IDS[number];
export type PathTileTextureGridType = 'square' | 'hex' | 'triangle';

export interface PathTileDecorationFlowerStyle {
  readonly kind: 'flowers';
  readonly bloomColors: readonly string[];
  readonly centerColor: string;
  readonly stemColor: string;
  readonly count: number;
}

export interface PathTileArtStyle {
  readonly atlasId: PathTileAtlasId;
  readonly label: string;
  readonly layerId: string;
  readonly aliases: readonly string[];
  readonly fillColor: string;
  readonly borderColor?: string;
  readonly borderWidthPx: number;
  readonly outerExpansionPx: number;
  readonly decoration?: PathTileDecorationFlowerStyle;
  readonly texturePath?: string;
  readonly texturePathByGrid?: Partial<Record<PathTileTextureGridType, string>>;
  readonly textureTileScale?: number;
}

export const PATH_TILE_ART_STYLES: readonly PathTileArtStyle[] = [
  {
    atlasId: 'dirt',
    label: 'Dirt Paths',
    layerId: 'path-dirt',
    aliases: ['path-dirt', 'dirt_paths'],
    fillColor: '#cb8d54',
    borderColor: '#a26234',
    borderWidthPx: 2,
    outerExpansionPx: 1,
  },
  {
    atlasId: 'water',
    label: 'Water',
    layerId: 'path-water',
    aliases: ['path-water', 'water_paths'],
    fillColor: '#316dab',
    borderColor: '#574b27',
    borderWidthPx: 2,
    outerExpansionPx: 1,
  },
  {
    atlasId: 'flowers',
    label: 'Flower Patch',
    layerId: 'path-flowers',
    aliases: ['path-flowers', 'flower_paths', 'flowers'],
    fillColor: '#4f8b43',
    borderWidthPx: 0,
    outerExpansionPx: 0,
    decoration: {
      kind: 'flowers',
      bloomColors: ['#f8f1ff', '#f7c7d9', '#f5d86d', '#b7d8ff'],
      centerColor: '#f2c95b',
      stemColor: '#6bad58',
      count: 8,
    },
  },
  {
    atlasId: 'brick_road',
    label: 'Brick Road',
    layerId: 'path-brick',
    aliases: ['path-brick', 'brick_road_paths'],
    fillColor: '#cb8d54', // used as alpha mask background
    borderColor: '#a26234', // used as alpha mask background for border
    borderWidthPx: 1,
    outerExpansionPx: 1,
    texturePathByGrid: {
      square: '/visual-assets/terrain/brick_road_square.svg',
      hex: '/visual-assets/terrain/brick_road_square.svg',
      triangle: '/visual-assets/terrain/brick_road_triangle.svg',
    },
    textureTileScale: 2,
  },
] as const;

const pathTileArtStyleById = new Map(PATH_TILE_ART_STYLES.map((style) => [style.atlasId, style] as const));

export function isPathTileAtlasId(value: string): value is PathTileAtlasId {
  return pathTileArtStyleById.has(value as PathTileAtlasId);
}

export function getPathTileArtStyle(atlasId: PathTileAtlasId): PathTileArtStyle {
  return pathTileArtStyleById.get(atlasId) ?? pathTileArtStyleById.get('dirt')!;
}

export function getPathTileTexturePath(
  atlasId: PathTileAtlasId,
  gridType: PathTileTextureGridType = 'square',
): string | undefined {
  const style = getPathTileArtStyle(atlasId);
  return style.texturePathByGrid?.[gridType] ?? style.texturePath;
}
